import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Siren, MonitorSmartphone, Clock, PlayCircle } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import Field, { inputCls } from '@/components/Field';
import { Reveal } from '@/components/Reveal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLanguage } from '@/context/LanguageContext';
import { API } from '@/lib/api';

const EMPTY = { name: '', phone: '', email: '', device: '', priority: 'normal', message: '' };

export default function Support() {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const faq = t('faqPage.items');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error(t('common.required')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/support`, form);
      toast.success(t('support.form.success'));
      setForm(EMPTY);
    } catch { toast.error(t('common.error')); } finally { setLoading(false); }
  };

  return (
    <div className="pt-28" data-testid="support-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="support.eyebrow" title="support.title" sub="support.sub" align="center" />
      </section>

      <section className="mx-auto grid max-w-7xl gap-6 px-5 py-14 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-7">
          <form onSubmit={submit} className="rounded-[2rem] border border-brand-line bg-white p-8 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.2)]" data-testid="support-form">
            <h3 className="font-display text-xl font-extrabold text-brand-ink">{t('support.form.title')}</h3>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label={t('support.form.name')} testid="support-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Field label={t('support.form.phone')} testid="support-phone-input" dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Field label={t('support.form.email')} testid="support-email-input" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Field label={t('support.form.device')} testid="support-device-input" dir="ltr" value={form.device} onChange={(e) => setForm({ ...form, device: e.target.value })} />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('support.form.priority')}</span>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="support-priority-select" className={inputCls}>
                    {t('support.form.priorities').map((p, i) => (
                      <option key={p} value={['low', 'normal', 'critical'][i]}>{p}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('support.form.message')}</span>
                  <textarea rows="4" data-testid="support-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </label>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="support-submit-btn" className="mt-6 w-full rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover hover:shadow-[0_16px_36px_-12px_rgba(0,82,255,0.6)] disabled:opacity-60">
              {loading ? t('common.submitting') : t('support.form.submit')}
            </button>
          </form>
        </Reveal>

        <div className="flex flex-col gap-5 lg:col-span-5">
          {t('support.showHotline') !== false && <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-gradient-to-br from-brand-crimson to-[#B91C1C] p-7 text-white" data-testid="support-hotline">
              <Siren className="h-7 w-7" />
              <h3 className="mt-4 font-display text-xl font-extrabold">{t('support.hotline.title')}</h3>
              <p className="mt-2 text-sm leading-relaxed text-red-100">{t('support.hotline.sub')}</p>
              <a href={`tel:${t('settings.hotline').replace(/\s/g, '')}`} data-testid="hotline-call-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-brand-crimson transition-transform duration-300 hover:scale-[1.03]">
                {t('support.hotline.call')} · <span dir="ltr" className="font-mono">{t('settings.hotline')}</span>
              </a>
            </div>
          </Reveal>}

          {t('support.showRemote') !== false && <Reveal delay={0.18}>
            <div className="rounded-[2rem] border border-brand-line bg-white p-7" data-testid="support-remote">
              <MonitorSmartphone className="h-7 w-7 text-brand-blue" />
              <h3 className="mt-4 font-display text-xl font-extrabold text-brand-ink">{t('support.remote.title')}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-steel">{t('support.remote.sub')}</p>
              <div className="mt-5 flex flex-wrap gap-2 font-mono text-[10px] font-bold uppercase tracking-widest">
                {t('support.remote.showAnydesk') !== false && <span className="rounded-full bg-brand-blue/8 border border-brand-blue/15 px-3 py-1.5 text-brand-blue">AnyDesk</span>}
                {t('support.remote.showTeamviewer') !== false && <span className="rounded-full bg-brand-blue/8 border border-brand-blue/15 px-3 py-1.5 text-brand-blue">TeamViewer</span>}
                {t('support.remote.anydesk') && <a href={t('support.remote.anydesk')} target="_blank" rel="noreferrer" data-testid="support-anydesk-link" className="rounded-full bg-brand-blue px-3 py-1.5 text-white">{t('support.remote.download')}</a>}
              </div>
            </div>
          </Reveal>}

          {t('support.showHours') !== false && <Reveal delay={0.24}>
            <div className="rounded-[2rem] border border-brand-line bg-white p-7" data-testid="support-hours">
              <Clock className="h-6 w-6 text-brand-teal" />
              <h3 className="mt-4 font-display text-lg font-extrabold text-brand-ink">{t('support.hours.title')}</h3>
              <p className="mt-2 text-sm text-brand-steel">{t('support.hours.sales')}</p>
              <p className="mt-1 text-sm font-bold text-brand-ink">{t('support.hours.support')}</p>
            </div>
          </Reveal>}

          {t('support.showVideos') !== false && <Reveal delay={0.3}>
            <div className="rounded-[2rem] border border-brand-line bg-white p-7" data-testid="support-videos">
              <h3 className="font-display text-lg font-extrabold text-brand-ink">{t('support.videos.title')}</h3>
              <ul className="mt-4 space-y-2.5">
                {t('support.videos.items').map((v, i) => (
                  <li key={i}>
                    <a href={v.url || '#'} target={v.url ? '_blank' : undefined} rel="noreferrer" onClick={(e) => { if (!v.url) { e.preventDefault(); toast.info(v.t, { description: 'WiyakGPS Help Center' }); } }} data-testid={`support-video-${i}`} className="flex w-full items-center gap-3 rounded-xl border border-brand-line bg-brand-paper px-4 py-3 text-start transition-colors hover:border-brand-blue/40">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-blue text-white"><PlayCircle className="h-4 w-4" /></span>
                      <span className="text-sm font-bold text-brand-ink">{v.t}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 pb-20 lg:px-8" data-testid="faq-section">
        <Reveal>
          <h2 className="font-display text-2xl font-extrabold text-brand-ink">{t('support.faq.title')}</h2>
          <Accordion type="single" collapsible className="mt-8">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-brand-line">
                <AccordionTrigger className="text-start text-base font-bold text-brand-ink hover:text-brand-blue hover:no-underline" data-testid={`faq-trigger-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-brand-steel" data-testid={`faq-content-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </div>
  );
}
